import React, { useEffect, useState, useRef } from 'react';
import api from '../api';
import SignatureCanvas from 'react-signature-canvas';
import { QRCodeSVG } from 'qrcode.react';
import { 
    Users, Search, Plus, Trash2, 
    FileText, X, Eye, EyeOff, 
    Settings, ShieldCheck, RefreshCw, 
    CheckCircle2, XCircle, QrCode, LogIn, Calendar, Printer, UserCheck, LogOut
} from 'lucide-react';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const PeopleManager = () => {
    const [activeTab, setActiveTab] = useState('roster'); 
    const [loading, setLoading] = useState(true);
    const [isOwner, setIsOwner] = useState(false);
    
    // 🔥 NEW: Store the current site ID explicitly
    const [currentSiteId, setCurrentSiteId] = useState(null);
    
    // Data
    const [people, setPeople] = useState([]);
    const [qualTypes, setQualTypes] = useState([]);
    const [attendanceLog, setAttendanceLog] = useState([]); 
    
    // Filters & UI
    const [search, setSearch] = useState('');
    const [expandedRow, setExpandedRow] = useState(null);
    const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0,10));

    // Upload State
    const [uploadingFor, setUploadingFor] = useState(null); 
    const [newCert, setNewCert] = useState({ type: '', custom: '', file: null, expiry: '' });

    // Config State
    const [newType, setNewType] = useState('');

    // --- KIOSK STATE ---
    const [isKioskMode, setIsKioskMode] = useState(false);
    const [showQR, setShowQR] = useState(false);
    const [kioskStep, setKioskStep] = useState(1); 
    const [kioskPin, setKioskPin] = useState('');
    const [kioskWorker, setKioskWorker] = useState(null);
    const sigPad = useRef({});

    useEffect(() => { loadData(); }, []);
    
    // Reload attendance when date changes or tab is opened
    useEffect(() => { 
        if(activeTab === 'attendance') loadAttendance(); 
    }, [dateFilter, activeTab, currentSiteId]);

    const generateSecurePin = () => Math.floor(100000 + Math.random() * 900000).toString();

    const loadData = async () => {
        setLoading(true);
        try {
            // 1. Fetch User Info
            const userRes = await api.get('users/me/');
            
            // Get Site ID
            const siteId = userRes.data.current_site_id || userRes.data.current_site;
            setCurrentSiteId(siteId);

            setIsOwner(userRes.data.is_superuser || (userRes.data.company_details?.type === 'OWNER'));

            // 2. Fetch Roster WITH SITE ID
            const [pplRes, typesRes] = await Promise.all([
                // 🔥 FIX: Pass the siteId query param
                api.get(`people/roster/?site_id=${siteId}`), 
                api.get('config/qualifications/')
            ]);
            
            setPeople(pplRes.data);
            setQualTypes(typesRes.data);
        } catch (err) { console.error(err); } 
        finally { setLoading(false); }
    };

    const loadAttendance = async () => {
        // Safety check: Don't load if we don't know the site yet
        if (!currentSiteId) return; 

        try {
            // 🔥 FIX: Add &site_id=... to the URL
            const res = await api.get(`attendance/?date=${dateFilter}&site_id=${currentSiteId}`);
            
            if (Array.isArray(res.data)) {
                setAttendanceLog(res.data);
            } else if (res.data && Array.isArray(res.data.results)) {
                setAttendanceLog(res.data.results);
            } else {
                setAttendanceLog([]);
            }
        } catch (err) {
            console.error("Failed to load attendance", err);
            setAttendanceLog([]);
        }
    };

    // --- ACTIONS ---
    const handleResetPin = async (person) => {
        // We now accept the whole object so we can read person.type
        if(!window.confirm(`Generate a new PIN for ${person.name}?`)) return;
        
        const newPin = generateSecurePin();
        try {
            await api.patch(`people/${person.id}/reset_pin/`, { 
                pin: newPin,
                type: person.type // Sends 'User' or 'Worker' to backend
            });
            
            // Update the UI immediately
            setPeople(prev => prev.map(p => p.id === person.id && p.type === person.type ? { ...p, pin: newPin } : p));
            alert(`New PIN: ${newPin}`);
        } catch(err) {
            console.warn("Backend update failed", err);
            alert("Failed to update PIN");
        }
    };

    const handleAddType = async (e) => {
        e.preventDefault();
        try {
            await api.post('config/qualifications/', { name: newType });
            setNewType('');
            const res = await api.get('config/qualifications/');
            setQualTypes(res.data);
        } catch(err) { alert("Failed"); }
    };

    const handleDeleteType = async (id) => {
        if(!window.confirm("Remove?")) return;
        try {
            await api.delete(`config/qualifications/${id}/`);
            setQualTypes(prev => prev.filter(t => t.id !== id));
        } catch(err) { alert("Failed"); }
    };

    const handleUploadCert = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('target_type', uploadingFor.type);
        formData.append('target_id', uploadingFor.raw_id);
        if (newCert.type === 'OTHER') formData.append('custom_name', newCert.custom);
        else formData.append('qualification_type', newCert.type);
        formData.append('file', newCert.file);
        if (newCert.expiry) formData.append('expiry_date', newCert.expiry);

        try {
            await api.post('people/upload-cert/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            alert("Uploaded");
            setUploadingFor(null);
            loadData();
        } catch(err) { alert("Failed"); }
    };

    // --- KIOSK ACTIONS ---

    const handlePinSubmit = (e) => {
        e.preventDefault();
        const found = people.find(p => p.pin === kioskPin);
        if (found) {
            setKioskWorker(found);
            setKioskStep(2);
        } else {
            alert("Invalid PIN.");
            setKioskPin('');
        }
    };

    const handleSignOn = async () => {
        if (sigPad.current.isEmpty()) return alert("Please sign first");
        const signatureData = sigPad.current.toDataURL();
        try {
            // Backend now returns { status: 'success', action: 'IN'/'OUT', name: '...' }
            const res = await api.post('attendance/sign_on/', { 
                pin: kioskWorker.pin, 
                signature: signatureData,
                site_id: currentSiteId // Ensure this is passed!
            });
            
            setLastAction(res.data.action); // Store 'IN' or 'OUT'
            setKioskStep(3);
            loadAttendance(); 
            
            setTimeout(() => {
                setKioskStep(1);
                setKioskPin('');
                setKioskWorker(null);
                setLastAction(''); // Reset
                if(sigPad.current) sigPad.current.clear(); // Clear signature
            }, 3000); // Give them 3 seconds to read "Signed Out"
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.error || "Sign-on failed.");
        }
    };

    // --- 🔥 UPDATED: DYNAMIC URL ---
    const getKioskUrl = () => {
        // Use the state we fetched from the API. 
        // If loading or null, fallback to 'select-site' to indicate an error state clearly.
        const id = currentSiteId || 'select-site'; 
        return `${window.location.origin}/kiosk/${id}`; 
    };

    const filteredPeople = people.filter(p => 
        p.name.toLowerCase().includes(search.toLowerCase()) || 
        p.company.toLowerCase().includes(search.toLowerCase()) ||
        p.role.toLowerCase().includes(search.toLowerCase())
    );

    const toggleRow = (id) => setExpandedRow(expandedRow === id ? null : id);
    // Add a state to store the action type ('IN' or 'OUT')
    const [lastAction, setLastAction] = useState('');
    // ==========================================
    // KIOSK OVERLAY (Internal Admin Version)
    // ==========================================
            // --- HELPER: Format Time ---
    const fmtTime = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    };
    if (isKioskMode) {

        return (
            <div style={{position:'fixed', top:0, left:0, width:'100vw', height:'100vh', background:'#020617', zIndex:9999, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'20px'}}>
                <button onClick={() => setIsKioskMode(false)} style={{position:'absolute', top:'20px', left:'20px', background:'transparent', border:'none', color:'#334155', cursor:'pointer'}}>Exit Kiosk</button>
                <div style={{width:'100%', maxWidth:'400px'}}>
                    {kioskStep === 1 && (
                        <div className="animate-fade-in" style={{textAlign:'center'}}>
                            <div style={{marginBottom:'30px', background:THEME.primary, width:'60px', height:'60px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px auto'}}><LogIn size={30} color="white" /></div>
                            <h2 style={{color:'white', marginBottom:'10px'}}>Site Access</h2>
                            <p style={{color:THEME.textSec, marginBottom:'30px'}}>Enter your 6-digit PIN</p>
                            <form onSubmit={handlePinSubmit}>
                                <input type="tel" maxLength="6" value={kioskPin} onChange={e => setKioskPin(e.target.value)} style={{width:'100%', padding:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`, background:THEME.card, color:'white', textAlign:'center', fontSize:'2rem', letterSpacing:'8px', height:'80px', marginBottom:'20px'}} placeholder="••••••" autoFocus />
                                <button type="submit" style={{width:'100%', padding:'15px', background:THEME.success, color:'white', border:'none', borderRadius:'8px', fontSize:'1.1rem', fontWeight:'bold', cursor:'pointer'}}>Next</button>
                            </form>
                        </div>
                    )}
                    {kioskStep === 2 && kioskWorker && (
                        <div className="animate-fade-in" style={{background:'white', borderRadius:'12px', overflow:'hidden'}}>
                            <div style={{background:THEME.primary, padding:'20px', textAlign:'center'}}>
                                <h3 style={{color:'white', margin:0}}>{kioskWorker.name}</h3>
                                <p style={{color:'rgba(255,255,255,0.8)', margin:0}}>{kioskWorker.company}</p>
                            </div>
                            <div style={{padding:'20px'}}>
                                <p style={{color:'#333', fontSize:'0.9rem', marginBottom:'10px', textAlign:'center'}}>I confirm I am fit for work.</p>
                                <div style={{border:'2px dashed #cbd5e1', borderRadius:'8px', background:'#f8fafc', marginBottom:'20px'}}>
                                    <SignatureCanvas ref={sigPad} penColor="black" canvasProps={{width: 320, height: 150, className: 'sigCanvas'}} />
                                </div>
                                <div style={{display:'flex', gap:'10px'}}>
                                    <button onClick={() => setKioskStep(1)} style={{flex:1, padding:'12px', background:'#f1f5f9', color:'#64748b', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Cancel</button>
                                    <button onClick={handleSignOn} style={{flex:1, padding:'12px', background:THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Check In</button>
                                </div>
                            </div>
                        </div>
                    )}
                    {kioskStep === 3 && (
                        <div className="animate-fade-in" style={{textAlign:'center'}}>
                            {lastAction === 'IN' ? (
                                /* --- SIGN IN SCREEN (Green) --- */
                                <div style={{background: 'rgba(16, 185, 129, 0.1)', padding:'40px', borderRadius:'12px', border:`2px solid ${THEME.success}`}}>
                                    <CheckCircle2 size={80} color={THEME.success} style={{marginBottom:'20px', display: 'inline-block'}} />
                                    <h2 style={{color:'white', margin:0, fontSize:'2rem'}}>SIGNED IN</h2>
                                    <p style={{color:THEME.textSec, fontSize:'1.1rem', marginTop:'10px'}}>
                                        Welcome to site, <strong style={{color:'white'}}>{kioskWorker?.name}</strong>.
                                    </p>
                                    <div style={{marginTop:'20px', color:THEME.success, fontWeight:'bold', fontSize:'1.2rem', padding:'10px', background:'rgba(0,0,0,0.3)', borderRadius:'8px'}}>
                                        Time: {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    </div>
                                </div>
                            ) : (
                                /* --- SIGN OUT SCREEN (Orange) --- */
                                <div style={{background: 'rgba(250, 204, 21, 0.1)', padding:'40px', borderRadius:'12px', border:`2px solid ${THEME.warning}`}}>
                                    <LogOut size={80} color={THEME.warning} style={{marginBottom:'20px', display: 'inline-block'}} />
                                    <h2 style={{color:'white', margin:0, fontSize:'2rem'}}>SIGNED OUT</h2>
                                    <p style={{color:THEME.textSec, fontSize:'1.1rem', marginTop:'10px'}}>
                                        Goodbye, <strong style={{color:'white'}}>{kioskWorker?.name}</strong>.
                                    </p>
                                    <div style={{marginTop:'20px', color:THEME.warning, fontWeight:'bold', fontSize:'1.2rem', padding:'10px', background:'rgba(0,0,0,0.3)', borderRadius:'8px'}}>
                                        Time: {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (loading) return <div style={{color:'white', padding:'20px'}}>Loading People...</div>;

    return (
        <div className="animate-fade-in" style={{padding:'20px'}}>
            {/* HEADER */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                <div style={{display:'flex', alignItems:'center', gap:'12px'}}><Users size={28} color={THEME.primary} /><h2 style={{color:'white', margin:0}}>People Manager</h2></div>
                {activeTab === 'roster' && (
                    <div style={{position:'relative', width:'300px'}}><Search size={16} style={{position:'absolute', left:'10px', top:'12px', color:THEME.textSec}}/><input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{padding:'10px 10px 10px 35px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background: THEME.card, color:'white', width:'100%', boxSizing:'border-box'}} /></div>
                )}
            </div>

            <div style={{display: 'flex', gap: '5px', marginBottom: '25px', background:'#020617', padding:'4px', borderRadius:'8px', border:`1px solid ${THEME.border}`, width: 'fit-content'}}>
                <button onClick={() => setActiveTab('roster')} style={{padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize:'0.9rem', fontWeight:600, background: activeTab === 'roster' ? THEME.primary : 'transparent', color: activeTab === 'roster' ? 'white' : THEME.textSec, display:'flex', alignItems:'center', gap:'6px'}}><Users size={16}/> Roster</button>
                <button onClick={() => setActiveTab('attendance')} style={{padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize:'0.9rem', fontWeight:600, background: activeTab === 'attendance' ? THEME.primary : 'transparent', color: activeTab === 'attendance' ? 'white' : THEME.textSec, display:'flex', alignItems:'center', gap:'6px'}}><UserCheck size={16}/> Attendance</button>
                {isOwner && <button onClick={() => setActiveTab('config')} style={{padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize:'0.9rem', fontWeight:600, background: activeTab === 'config' ? THEME.primary : 'transparent', color: activeTab === 'config' ? 'white' : THEME.textSec, display:'flex', alignItems:'center', gap:'6px'}}><Settings size={16}/> Config</button>}
            </div>

            {/* TAB 1: ROSTER - Unchanged */}
            {activeTab === 'roster' && (
                <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflowX:'auto'}}>
                    <table style={{width:'100%', borderCollapse:'collapse', color:'white', fontSize:'0.9rem'}}>
                        <thead style={{background:'#0f172a', borderBottom:`1px solid ${THEME.border}`}}>
                            <tr>
                                <th style={{padding:'15px', textAlign:'left'}}>Name</th>
                                <th style={{padding:'15px', textAlign:'left'}}>Role</th>
                                <th style={{padding:'15px', textAlign:'left'}}>Company</th>
                                <th style={{padding:'15px', textAlign:'center'}}>Access PIN</th>
                                <th style={{padding:'15px', textAlign:'center'}}>Inducted</th>
                                <th style={{padding:'15px', textAlign:'center'}}>Certs</th>
                                <th style={{padding:'15px', textAlign:'right'}}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPeople.map(p => (
                                <React.Fragment key={p.id}>
                                    <tr style={{borderBottom:`1px solid ${THEME.border}`, background: expandedRow === p.id ? '#1e293b' : 'transparent'}}>
                                        <td style={{padding:'15px', fontWeight:'bold'}}>{p.name}</td>
                                        <td style={{padding:'15px', color:THEME.textSec}}>{p.role}</td>
                                        <td style={{padding:'15px'}}>{p.company}</td>
                                        <td style={{padding:'15px', textAlign:'center'}}>
                                            <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}>
                                                <span style={{background:'#020617', padding:'4px 10px', borderRadius:'4px', fontFamily:'monospace', fontSize:'0.9rem', border:`1px solid ${THEME.border}`, letterSpacing:'2px'}}>{p.pin || '------'}</span>
                                                <button onClick={(e) => { e.stopPropagation(); handleResetPin(p); }} title="Generate New PIN" style={{background:'transparent', border:'none', color:THEME.textSec, cursor:'pointer', padding:'4px'}}><RefreshCw size={14}/></button>
                                            </div>
                                        </td>
                                        <td style={{padding:'15px', textAlign:'center'}}>{p.is_inducted ? <CheckCircle2 size={18} color={THEME.success} style={{margin:'0 auto'}}/> : <XCircle size={18} color={THEME.danger} style={{margin:'0 auto'}}/>}</td>
                                        <td style={{padding:'15px', textAlign:'center'}}>{p.qualifications.length > 0 ? <span style={{fontWeight:'bold', color: THEME.success}}>{p.qualifications.length}</span> : <span style={{color: THEME.textSec}}>-</span>}</td>
                                        <td style={{padding:'15px', textAlign:'right'}}>
                                            <button onClick={() => toggleRow(p.id)} style={{background:'transparent', border:`1px solid ${THEME.primary}`, color:THEME.primary, padding:'5px 10px', borderRadius:'4px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', float:'right'}}>{expandedRow === p.id ? <EyeOff size={14}/> : <Eye size={14}/>} {expandedRow === p.id ? 'Hide' : 'View'}</button>
                                        </td>
                                    </tr>
                                    {expandedRow === p.id && (
                                        <tr style={{background:'#0f172a', borderBottom:`1px solid ${THEME.border}`}}>
                                            <td colSpan="7" style={{padding:'20px'}}>
                                                {/* Details... */}
                                                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'15px', alignItems:'center'}}>
                                                    <h4 style={{margin:0, color:'white', display:'flex', alignItems:'center', gap:'8px'}}><ShieldCheck size={18} color={THEME.primary}/> Qualifications</h4>
                                                    <button onClick={() => { setUploadingFor(p); setNewCert({ ...newCert, type: qualTypes[0]?.id || '' }); }} style={{background:THEME.primary, color:'white', border:'none', padding:'6px 12px', borderRadius:'6px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px'}}><Plus size={16}/> Add Cert</button>
                                                </div>
                                                <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'15px'}}>
                                                    {p.qualifications.map(q => (
                                                        <div key={q.id} style={{background:THEME.card, padding:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`}}>
                                                            <div style={{fontWeight:'bold', color:'white', marginBottom:'5px'}}>{q.name}</div>
                                                            <div style={{fontSize:'0.8rem', color:THEME.textSec}}>Expires: {q.expiry || 'N/A'}</div>
                                                            {q.file && <a href={q.file} target="_blank" rel="noreferrer" style={{display:'flex', alignItems:'center', gap:'6px', marginTop:'8px', fontSize:'0.8rem', color:THEME.primary, textDecoration:'none'}}><FileText size={14}/> View</a>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* TAB 2: ATTENDANCE & QR */}
            {activeTab === 'attendance' && (
                <div style={{display:'grid', gridTemplateColumns: '3fr 1fr', gap:'20px'}}>
                    <div className="card" style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, padding:'20px'}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                            <h3 style={{color:'white', margin:0}}>Daily Register</h3>
                            <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                <Calendar size={16} color={THEME.textSec}/>
                                <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} style={{background:'#020617', border:`1px solid ${THEME.border}`, color:'white', padding:'8px', borderRadius:'6px'}} />
                            </div>
                        </div>
                        <table style={{width:'100%', borderCollapse:'collapse', color:'white', fontSize:'0.9rem'}}>
                            <thead style={{borderBottom:`1px solid ${THEME.border}`, textAlign:'left'}}>
                                <tr>
                                    {/* Updated Headers */}
                                    <th style={{padding:'10px'}}>Name</th>
                                    <th>Company</th>
                                    <th>Sign In</th>
                                    <th>Sign Out</th>
                                    <th style={{textAlign:'center'}}>On Site?</th>
                                    <th>Signature</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attendanceLog.length === 0 && <tr><td colSpan="6" style={{padding:'20px', textAlign:'center', color:THEME.textSec}}>No sign-ins for this date.</td></tr>}
                                {attendanceLog.map(log => (
                                    <tr key={log.id} style={{borderBottom:`1px solid ${THEME.border}44`}}>
                                        <td style={{padding:'12px', fontWeight:'bold'}}>{log.name}</td>
                                        <td>{log.company}</td>
                                        
                                        {/* NEW: Time Columns */}
                                        <td style={{color: THEME.success}}>{fmtTime(log.sign_in_time)}</td>
                                        <td style={{color: THEME.textSec}}>{fmtTime(log.sign_out_time)}</td>
                                        
                                        {/* NEW: Status Column */}
                                        <td style={{textAlign:'center'}}>
                                            {!log.sign_out_time ? (
                                                <span style={{background:'rgba(16, 185, 129, 0.2)', color: THEME.success, padding:'2px 8px', borderRadius:'4px', fontSize:'0.75rem', fontWeight:'bold'}}>
                                                    YES
                                                </span>
                                            ) : (
                                                <span style={{background:'rgba(148, 163, 184, 0.1)', color: THEME.textSec, padding:'2px 8px', borderRadius:'4px', fontSize:'0.75rem'}}>
                                                    NO
                                                </span>
                                            )}
                                        </td>

                                        <td style={{padding:'5px'}}>
                                            {log.signature ? (
                                                <img src={log.signature} alt="sig" style={{height:'30px', background:'white', borderRadius:'4px', border:`1px solid ${THEME.textSec}`}} />
                                            ) : ( <span style={{color:THEME.textSec}}>Missing</span> )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div style={{display:'flex', flexDirection:'column', gap:'20px'}}>
                        <div style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.primary}`}}>
                            <h4 style={{color:'white', marginTop:0}}>Kiosk Mode</h4>
                            <p style={{color:THEME.textSec, fontSize:'0.85rem'}}>Open the public sign-on page.</p>
                            {/* 🔥 UPDATED BUTTON: Opens the dynamic public link in new tab */}
                            <button onClick={() => window.open(getKioskUrl(), '_blank')} style={{width:'100%', padding:'12px', background:'white', color:'black', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer', display:'flex', justifyContent:'center', alignItems:'center', gap:'10px'}}>
                                <LogIn size={18}/> Launch Kiosk
                            </button>
                        </div>

                        <div style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`}}>
                            <h4 style={{color:'white', marginTop:0}}>QR Poster</h4>
                            <p style={{color:THEME.textSec, fontSize:'0.85rem'}}>Print this for workers to scan.</p>
                            <button onClick={() => setShowQR(true)} style={{width:'100%', padding:'12px', background:THEME.primary, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer', display:'flex', justifyContent:'center', alignItems:'center', gap:'10px'}}>
                                <Printer size={18}/> View & Print
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 3: CONFIG & MODALS (Unchanged) */}
            {activeTab === 'config' && isOwner && (
                <div style={{maxWidth:'600px'}}>
                      <div style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`, marginBottom:'20px'}}>
                        <h4 style={{marginTop:0, color:'white'}}>Add Qualification Type</h4>
                        <form onSubmit={handleAddType} style={{display:'flex', gap:'10px'}}>
                            <input value={newType} onChange={e => setNewType(e.target.value)} style={{flex:1, padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white'}} required placeholder="e.g. White Card" />
                            <button type="submit" style={{background:THEME.success, color:'white', border:'none', padding:'0 20px', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Add</button>
                        </form>
                    </div>
                    {qualTypes.map(t => (
                        <div key={t.id} style={{padding:'15px', background:THEME.card, borderRadius:'8px', border:`1px solid ${THEME.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
                            <span style={{color:'white'}}>{t.name}</span>
                            <button onClick={() => handleDeleteType(t.id)} style={{background:'transparent', border:'none', color:THEME.textSec, cursor:'pointer'}}><Trash2 size={18}/></button>
                        </div>
                    ))}
                </div>
            )}

            {/* UPLOAD MODAL (Unchanged) */}
            {uploadingFor && (
                <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.8)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:100}}>
                    <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', width:'400px', border:`1px solid ${THEME.border}`}}>
                        {/* ... (Upload form code remains identical) ... */}
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                            <h4 style={{margin:0, color:'white'}}>Add Cert for {uploadingFor.name}</h4>
                            <button onClick={() => setUploadingFor(null)} style={{background:'transparent', border:'none', color:THEME.textSec, cursor:'pointer'}}><X size={20}/></button>
                        </div>
                        <form onSubmit={handleUploadCert}>
                            <select value={newCert.type} onChange={e => setNewCert({...newCert, type: e.target.value})} style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', marginBottom:'10px'}}>
                                {qualTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                <option value="OTHER">Other</option>
                            </select>
                            {newCert.type === 'OTHER' && <input placeholder="Custom Name" value={newCert.custom} onChange={e => setNewCert({...newCert, custom: e.target.value})} style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', marginBottom:'10px', boxSizing:'border-box'}} required />}
                            <input type="date" value={newCert.expiry} onChange={e => setNewCert({...newCert, expiry: e.target.value})} style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', marginBottom:'10px', boxSizing:'border-box'}} />
                            <input type="file" onChange={e => setNewCert({...newCert, file: e.target.files[0]})} style={{width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', marginBottom:'20px', boxSizing:'border-box'}} required />
                            <button type="submit" style={{width:'100%', padding:'10px', background:THEME.success, border:'none', color:'white', borderRadius:'6px', cursor:'pointer', fontWeight:'bold'}}>Upload</button>
                        </form>
                    </div>
                </div>
            )}

            {/* QR POSTER MODAL (🔥 UPDATED to use dynamic URL) */}
            {showQR && (
                <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.8)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:100}}>
                    <div style={{background:'white', padding:'40px', borderRadius:'12px', textAlign:'center', maxWidth:'400px'}}>
                        <h2 style={{color:'black', margin:'0 0 10px 0'}}>SITE SIGN IN</h2>
                        <div style={{margin:'20px auto', padding:'10px', border:'2px solid black', display:'inline-block'}}>
                            {/* 🔥 NOW USES THE CURRENT SITE ID */}
                            <QRCodeSVG value={getKioskUrl()} size={200} />
                        </div>
                        <p style={{fontSize:'0.8rem', color:'#555', marginTop:'10px'}}>{getKioskUrl()}</p>
                        <div style={{display:'flex', gap:'10px', justifyContent:'center', marginTop:'20px'}}>
                            <button onClick={() => window.print()} style={{background:'#3b82f6', color:'white', border:'none', padding:'10px 20px', borderRadius:'6px', cursor:'pointer'}}>Print</button>
                            <button onClick={() => setShowQR(false)} style={{background:'#ef4444', color:'white', border:'none', padding:'10px 20px', borderRadius:'6px', cursor:'pointer'}}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PeopleManager;